import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Shield,
  Mail,
  Lock,
  Visibility,
  People,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const handleContactSupport = () => {
    const supportUrl = process.env.REACT_APP_SUPPORT_URL || 'mailto:support@promopeek.com';
    window.open(supportUrl, '_blank');
  };

  const sections = [
    {
      icon: Mail,
      title: "Information We Collect",
      content: [
        "When you connect your Gmail account to PromoPeek, we access the following information:",
        "• Your email address",
        '• Emails categorized under the "Promotions" tab',
        "• Metadata like subject lines, sender addresses, and timestamps",
        "",
        "We do not access personal or sensitive emails outside the Promotions tab, nor do we read or store your entire inbox.",
      ],
    },
    {
      icon: Visibility,
      title: "How We Use Your Information",
      content: [
        "We use your promotional emails to:",
        "• Identify and summarize active deals, discounts, and promotions",
        "• Provide you a simplified view of relevant promotional content",
        "",
        "We do not use your data for advertising or marketing. We do not share or sell your email content to third parties.",
      ],
    },
    {
      icon: Lock,
      title: "Data Storage",
      content: [
        "PromoPeek processes your data temporarily during your session. We do not permanently store your email content on our servers.",
      ],
    },
    {
      icon: People,
      title: "Third-Party Services",
      content: [
        "PromoPeek uses the Google Gmail API to access your emails. Use of this data is subject to:",
        "• Google API Services User Data Policy",
        "• Google's Privacy Policy",
        "",
        "We comply with Google's limited use requirements. Google user data is not shared with any other third parties for marketing, analytics, or advertising purposes.",
      ],
    },
    {
      icon: Settings,
      title: "Your Choices",
      content: [
        "You can disconnect your Gmail account at any time through your Google Account settings:",
        "• Google Account Permissions",
        "",
        "Once disconnected, PromoPeek no longer has access to your emails.",
      ],
    },
    {
      icon: Shield,
      title: "Security",
      content: ["We take reasonable steps to protect your data using encryption and secure authentication mechanisms."],
    },
    {
      icon: People,
      title: "Sharing & Disclosure",
      content: [
        "We do not sell, rent, or share your Gmail data with third parties for advertising or marketing.",
        "We disclose data only in limited cases: (a) service providers strictly necessary to operate PromoPeek—each bound by confidentiality and Google Limited-Use obligations; (b) legal requirements or safety concerns; (c) aggregated statistics that contain no personal data.",
        "Outside of these situations, we do not transfer or disclose Google user data.",
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #ffe4e6, #faf5ff, #f0f9ff)' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid',
          borderColor: 'pink.100',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  color: 'grey.600',
                  '&:hover': { bgcolor: 'pink.100' },
                  borderRadius: '50%',
                }}
              >
                <ArrowBack />
              </IconButton>
              <Avatar
                src="/images/promopeak-logo.png"
                alt="PromoPeek Logo"
                sx={{ width: 40, height: 40, borderRadius: 1 }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(90deg, #ec4899, #a855f7)',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                PromoPeek
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                color: 'white',
              }}
            >
              <Shield sx={{ width: 32, height: 32 }} />
            </Avatar>
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'grey.900', mb: 2 }}>
            Privacy Policy
          </Typography>
          <Typography variant="h6" sx={{ color: 'grey.600', mb: 1 }}>
            Your privacy matters to us! 🐹
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.500' }}>
            Last updated: July 2025
          </Typography>
        </Box>

        {/* Introduction */}
        <Card
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(8px)',
            border: '1px solid',
            borderColor: 'pink.100',
            borderRadius: 3,
            mb: 4,
          }}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'grey.700', lineHeight: 1.7 }}>
              PromoPeek is committed to protecting your privacy. This Privacy Policy explains how
              we collect, use, and protect your information.
            </Typography>
          </CardContent>
        </Card>

        {/* Privacy Sections */}
        <Box sx={{ mb: 6 }}>
          {sections.map((section, index) => (
            <Card
              key={index}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(8px)',
                border: '1px solid',
                borderColor: 'pink.100',
                borderRadius: 3,
                mb: 3,
                '&:hover': {
                  boxShadow: 3,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                      color: 'white',
                      flexShrink: 0,
                    }}
                  >
                    <section.icon sx={{ width: 24, height: 24 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'grey.800', mb: 2 }}>
                      {index + 1}. {section.title}
                    </Typography>
                    <Box sx={{ '& > p': { mb: 1 } }}>
                      {section.content.map((paragraph, pIndex) => (
                        <Typography key={pIndex} variant="body1" sx={{ color: 'grey.700', lineHeight: 1.7 }}>
                          {paragraph}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Contact Section */}
        <Card
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            border: '1px solid',
            borderColor: 'pink.200',
            borderRadius: 3,
            boxShadow: 3,
          }}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                background: 'linear-gradient(135deg, #fce7f3, #f3e8ff)',
                color: '#ec4899',
                mx: 'auto',
                mb: 3,
              }}
            >
              <Mail sx={{ width: 32, height: 32 }} />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'grey.800', mb: 2 }}>
              Contact Us
            </Typography>
            <Typography variant="body1" sx={{ color: 'grey.600', mb: 3 }}>
              If you have any questions or concerns about this Privacy Policy, please don't hesitate to reach out!
            </Typography>
            <Button
              variant="contained"
              onClick={handleContactSupport}
              sx={{
                borderRadius: '25px',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                color: 'white',
                boxShadow: '0 4px 12px rgba(236,72,153,0.3)',
                background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(236,72,153,0.4)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Mail sx={{ mr: 1, width: 20, height: 20 }} />
              Contact Support
            </Button>
          </CardContent>
        </Card>

        {/* Cute Footer */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 1.5,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(8px)',
              borderRadius: '25px',
              border: '1px solid',
              borderColor: 'pink.100',
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    bgcolor: '#ec4899',
                    borderRadius: '50%',
                  }}
                />
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'grey.600' }}>
              Your privacy is safe with your hamster friend! 🐹🔒
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}